import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  DeleteOutlineIcon,
  SearchIcon,
  CircularProgress,
  Stack,
  Autocomplete,
  AnchorIcon,
  DatePickerMobi
} from '@esgian/esgianui';
import moment from 'moment';

const OngoingBulkSearchCards = ({
  voyageData,
  vessels,
  onDataChange,
  onSearch,
  isSearching,
  hasSearched
}) => {
  const handleInputChange = (id, field, value) => {
    // Apply the changes immediately to the voyage data
    const updatedData = voyageData.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          [field]: value,
          // Reset the resolved entity when the field changes
          ...(field === 'vesselName' && { resolvedVessel: null, vesselImo: null })
        };
      }
      return item;
    });

    onDataChange(updatedData);
  };

  const handleDateChange = (id, field, value) => {
    // Format the date as YYYY-MM-DD
    const formattedDate = value ? moment(value).format('YYYY-MM-DD') : '';

    // Apply the changes immediately to the voyage data
    const updatedData = voyageData.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          [field]: formattedDate
        };
      }
      return item;
    });

    onDataChange(updatedData);
  };

  const handleVesselSelectionChange = (id, vessel) => {
    // Update voyage data when vessel is selected from dropdown
    const updatedData = voyageData.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          vesselName: vessel ? vessel.name : '',
          vesselImo: vessel ? vessel.imo.toString() : null,
          resolvedVessel: vessel
            ? {
                imo: vessel.imo,
                name: vessel.name,
                operatorId: vessel.operatorId,
                operatorName: vessel.operatorName
              }
            : null
        };
      }
      return item;
    });

    onDataChange(updatedData);
  };

  const handleDeleteItem = (id) => {
    const updatedData = voyageData.filter((item) => item.id !== id);
    onDataChange(updatedData);
  };

  const getCardStatusColor = (item) => {
    if (item.resolvedVessel) {
      return 'success.main';
    } else if (!item.isValid) {
      return 'error.main';
    } else if (hasSearched) {
      return 'error.main';
    }
    return 'text.secondary';
  };

  const getCardStatusText = (item) => {
    if (item.resolvedVessel) {
      return 'Valid';
    } else if (!item.isValid) {
      return 'Missing Data';
    } else if (hasSearched) {
      return 'Invalid';
    }
    return 'Pending';
  };

  // Helper function to format dates or return empty string for invalid dates
  const getFormattedDate = (dateString) => {
    if (!dateString) return '';
    const date = moment(dateString);
    return date.isValid() ? date.format('YYYY-MM-DD') : '';
  };

  // Filter function for autocomplete to filter vessels
  const filterVessels = (options, { inputValue }) => {
    const inputValueLower = inputValue.toLowerCase();
    return options.filter(
      (option) =>
        (option.name && option.name.toLowerCase().includes(inputValueLower)) ||
        (option.imo && option.imo.toString().includes(inputValueLower))
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Voyage Bulk Search Data ({voyageData.length} items)</Typography>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={onSearch}
          disabled={isSearching || voyageData.length === 0}>
          {isSearching ? 'Searching...' : 'Perform Bulk Search'}
        </Button>
      </Stack>

      {isSearching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {voyageData.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <Paper
                variant="outlined"
                sx={{
                  position: 'relative',
                  borderColor: (theme) =>
                    !item.isValid ? theme.palette.error.main : theme.palette.divider,
                  backgroundColor: !item.isValid ? 'rgba(211, 47, 47, 0.04)' : 'inherit',
                  '&:hover': {
                    borderColor: !item.isValid ? 'error.main' : 'primary.main',
                    boxShadow: 1
                  },
                  p: 2
                }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                  <Typography variant="caption" color={getCardStatusColor(item)}>
                    {getCardStatusText(item)}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* Vessel */}
                  <Grid item xs={12}>
                    <Autocomplete
                      options={vessels || []}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : `${option.name || ''} (${option.imo}) ${
                              option.operatorName ? `- ${option.operatorName}` : ''
                            }`
                      }
                      filterOptions={filterVessels}
                      value={
                        item.resolvedVessel
                          ? vessels.find((v) => v.imo === item.resolvedVessel.imo)
                          : null
                      }
                      onChange={(e, newValue) => handleVesselSelectionChange(item.id, newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Vessel"
                          value={item.vesselName || item.vesselImo || ''}
                          onChange={(e) => handleInputChange(item.id, 'vesselName', e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <AnchorIcon fontSize="small" sx={{ mr: 1 }} />
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Date range */}
                  <Grid item xs={12}>
                    <DatePickerMobi
                      sx={{
                        inputProps: {
                          sx: { width: '100%' },
                          InputProps: {},
                          label: 'Start Date',
                          placeholder: 'Select date',
                          defaultValue: undefined,
                          value: getFormattedDate(item.startDate)
                        }
                      }}
                      onClose={({ value }) => {
                        handleDateChange(item.id, 'startDate', value);
                      }}
                      selectedDate={
                        item.startDate && moment(item.startDate).isValid()
                          ? moment(item.startDate)
                          : null
                      }
                      dateFormat="YYYY-MM-DD"
                      autoClose
                    />
                  </Grid>

                  {/* Delete button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => handleDeleteItem(item.id)}>
                        Delete
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

OngoingBulkSearchCards.propTypes = {
  voyageData: PropTypes.arrayOf(PropTypes.object).isRequired,
  vessels: PropTypes.arrayOf(PropTypes.object),
  onDataChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  isSearching: PropTypes.bool,
  hasSearched: PropTypes.bool
};

OngoingBulkSearchCards.defaultProps = {
  vessels: [],
  isSearching: false,
  hasSearched: false
};

export default OngoingBulkSearchCards;
